import {useState, useEffect, useCallback} from 'react';

import {getCreditCardErrorText} from '@optimaxdev/utils';

import {STRIPE_SCRIPT_SRC} from 'constants/checkout';
import {useLoadScript} from 'hooks/useLoadScript/useLoadScript';
import {
    BillingDetailsType,
    ElementStripeCreateType,
    ErrorsStateType,
    InitStripeType,
    OptionType,
    PaymentMethodType,
} from 'types/stripeType';

/**
 * useInitStripe hook, which provide methods for create and mount fields.
 *
 * @param {FieldType} publishableKey - properties
 * @returns {InitStripeType} stripe exemplar
 */
const useInitStripe = (publishableKey: string) => {
    const isScriptLoaded = useLoadScript(STRIPE_SCRIPT_SRC);
    const [Stripe, initStripe] = useState<InitStripeType>();

    useEffect(() => {
        if (isScriptLoaded && window?.Stripe) {
            const {createPaymentMethod, elements} = window.Stripe(publishableKey, {locale: 'en'});
            const {create} = elements();
            // @ts-expect-error
            initStripe({create, createPaymentMethod});
        }
    }, [isScriptLoaded]);

    return Stripe;
};
/**
 * PaymentsOS hooks, which provide payment os fields
 *
 * @param {FieldType} publishableKey - properties
 * @param {PropsType} options - properties
 * @returns {JSX.Element} component
 */
export const useStripe = (publishableKey: string, {settings}: OptionType) => {
    const Stripe = useInitStripe(publishableKey);

    const [errors, setErrors] = useState<ErrorsStateType>({
        cardCvc: '',
        cardExpiry: '',
        cardNumber: '',
        currentError: '',
    });

    const [cardNumberField, saveCardNumberField] = useState<ElementStripeCreateType>();
    const [isCreateTokenLoading, setCreateTokenLoading] = useState(false);

    useEffect(() => {
        if (Stripe) {
            Object.entries(settings).forEach(([field, {id, option, type}]) => {
                const createdField = Stripe.create(type, option);

                createdField.mount(id);

                createdField.on('change', ({elementType, empty, error}) => {
                    setErrors(
                        (prevState): ErrorsStateType => ({
                            ...prevState,
                            currentError: error
                                ? prevState.cardNumber ||
                                  getCreditCardErrorText(error?.message || '')
                                : '',
                            [elementType]:
                                elementType === 'cardCvc' && empty
                                    ? "Please enter your card's security code (CVV/CID)"
                                    : getCreditCardErrorText(error?.message || ''),
                        }),
                    );
                });

                if (field === 'pan') {
                    saveCardNumberField(createdField);
                }
            });
        }
    }, [Stripe]);

    const createToken = useCallback(
        (
            billingDetails: BillingDetailsType,
            {
                onReject,
                onSuccess,
            }: {
                onSuccess?: (arg: PaymentMethodType['paymentMethod']) => void;
                onReject?: (arg: unknown) => void;
            },
        ) => {
            if (cardNumberField && Stripe) {
                setCreateTokenLoading(true);

                Stripe.createPaymentMethod({
                    billing_details: billingDetails,
                    card: cardNumberField,
                    type: 'card',
                })
                    .then(({paymentMethod}) => {
                        if (onSuccess) {
                            onSuccess(paymentMethod);
                        }
                    })
                    .catch(error => {
                        if (onReject) {
                            onReject(error);
                        }
                        setCreateTokenLoading(false);
                    })
                    .finally(() => {
                        setCreateTokenLoading(false);
                        setErrors(prevState => ({
                            ...prevState,
                            currentError:
                                prevState.cardNumber || prevState.cardExpiry || prevState.cardCvc,
                        }));
                    });
            }
            return null;
        },
        [cardNumberField, Stripe],
    );

    return {
        createToken,
        error: errors.currentError,
        isCreateTokenLoading,
    };
};
